import {
  Badge,
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { ChevronDown, Search } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  PAYER_TRANSACTIONS,
  PAYER_TRANSACTION_LABELS,
  type PayerTransaction,
} from './payerEnrollments';

interface EnrollmentFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  clinics: { id: string; name: string }[];
  selectedClinicIds: string[];
  onClinicIdsChange: (value: string[]) => void;
  selectedTransactions: PayerTransaction[];
  onTransactionsChange: (value: PayerTransaction[]) => void;
  /** Buttons for the far end of the row, such as Refresh. */
  actions?: ReactNode;
}

/** Search, the transaction chip, and the clinic chip when there is more than one clinic. */
export function EnrollmentFilters({
  search,
  onSearchChange,
  clinics,
  selectedClinicIds,
  onClinicIdsChange,
  selectedTransactions,
  onTransactionsChange,
  actions,
}: EnrollmentFiltersProps) {
  return (
    <div className='flex flex-col gap-3 md:flex-row md:items-center'>
      <div className='relative w-full md:w-80'>
        <Search className='absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder='Search by payer name or ID'
          className='pl-8'
          aria-label='Search payers'
        />
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <FilterChip
          label='Transaction'
          options={PAYER_TRANSACTIONS.map(transaction => ({
            value: transaction,
            label: PAYER_TRANSACTION_LABELS[transaction],
          }))}
          selected={selectedTransactions}
          onChange={onTransactionsChange}
        />
        {clinics.length > 1 && (
          <FilterChip
            label='Clinic'
            options={clinics.map(c => ({ value: c.id, label: c.name }))}
            selected={selectedClinicIds}
            onChange={onClinicIdsChange}
          />
        )}
      </div>
      {actions && <div className='md:ml-auto'>{actions}</div>}
    </div>
  );
}

interface FilterChipProps<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (value: T[]) => void;
}

function FilterChip<T extends string>({
  label,
  options,
  selected,
  onChange,
}: FilterChipProps<T>) {
  const toggle = (value: T) =>
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    );

  return (
    <Popover>
      {/* Both the chip and the search input keep the design system's default
          size, so the row sits on one baseline: Button sm is h-6, Input sm is
          h-7, and the two never lined up. */}
      <PopoverTrigger asChild>
        <Button variant='outline' className='whitespace-nowrap'>
          {label}
          {selected.length > 0 && (
            <Badge variant='destructive' className='ml-2'>
              {selected.length}
            </Badge>
          )}
          <ChevronDown className='ml-1 h-3 w-3' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-64 space-y-1'>
        <p className='px-1 pb-1 text-sm text-muted-foreground'>
          Filter by {label.toLowerCase()}
        </p>
        {options.map(option => (
          <label
            key={option.value}
            className='flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted'
          >
            <Checkbox
              checked={selected.includes(option.value)}
              onCheckedChange={() => toggle(option.value)}
              checkboxSize='sm'
            />
            {option.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}
